import { useMemo } from "react";
import {
  NavigationFlow,
  useDeviceType,
  useNavigationFlow,
} from "@b2bportal/core-utilities";
import type { CombinedPackageSteps } from "@/pages/Packages";

type BreadcrumbStepData = {
  step: string;
  label: string;
  isSelected: boolean;
  onClick: () => void;
};

type UsePackageBreadcrumbsResponse = {
  breadcrumbSteps: Array<BreadcrumbStepData>;
};

export const usePackageBreadcrumbs = (
  currentStep: CombinedPackageSteps
): UsePackageBreadcrumbsResponse => {
  const { isMobile } = useDeviceType();
  const {
    flow: { stepOrder, steps },
  } = useNavigationFlow();

  const goToStep = NavigationFlow.useGoToStep();

  const packageBreadcrumbSteps = useMemo(() => {
    return stepOrder.reduce<BreadcrumbStepData[]>((acc, step) => {
      const stepData = steps[step];
      if (stepData.breadcrumb) {
        acc.push({
          step: step,
          label: stepData.breadcrumb.label,
          onClick: () => goToStep(step, false),
          isSelected: step === currentStep,
        });
      }
      return acc;
    }, []);
  }, [currentStep, goToStep, stepOrder, steps]);

  const breadcrumbSteps = useMemo(() => {
    if (!isMobile || packageBreadcrumbSteps.length <= 3) {
      return packageBreadcrumbSteps;
    }
    const currentIndex = packageBreadcrumbSteps.findIndex((s) => s.isSelected);
    if (currentIndex === -1) {
      return packageBreadcrumbSteps;
    }
    // Mobile: show 3 steps centered on current when possible, else first or last 3
    const startIndex = Math.max(
      0,
      Math.min(currentIndex - 1, packageBreadcrumbSteps.length - 3)
    );
    return packageBreadcrumbSteps.slice(startIndex, startIndex + 3);
  }, [packageBreadcrumbSteps, isMobile]);

  return {
    breadcrumbSteps,
  };
};
